import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { forwardRef, useEffect, useRef, useState } from "react";
import { styled } from "styled-components";

interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  inputType?: "numeric" | "alphanumeric";
}

const CodeInput = forwardRef<HTMLDivElement, CodeInputProps>(
  (
    {
      length = 8,
      value,
      onChange,
      onComplete,
      disabled = false,
      error = false,
      inputType = "numeric",
    },
    ref,
  ) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Update activeIndex when value changes
    useEffect(() => {
      const nextIndex = value.length < length ? value.length : length - 1;
      setActiveIndex(nextIndex);
    }, [value, length]);

    // Call onComplete when all digits are filled
    useEffect(() => {
      if (value.length === length && onComplete) {
        onComplete(value);
      }
    }, [value, length, onComplete]);

    const handleChange = (index: number, char: string) => {
      // Validate input based on inputType
      if (char) {
        if (inputType === "numeric" && !/^\d$/.test(char)) {
          return;
        }
        if (inputType === "alphanumeric" && !/^[a-zA-Z0-9]$/.test(char)) {
          return;
        }
      }

      const newValue = value.split("");
      newValue[index] = char;
      const result = newValue.join("").slice(0, length);
      onChange(result);

      // Auto-focus next input
      if (char && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (e.key === "Backspace") {
        e.preventDefault();

        if (value[index]) {
          // If current box has value, clear it
          const newValue = value.split("");
          newValue[index] = "";
          onChange(newValue.join(""));
        } else if (index > 0) {
          // If current box is empty, move to previous box and clear it
          const newValue = value.split("");
          newValue[index - 1] = "";
          onChange(newValue.join(""));
          inputRefs.current[index - 1]?.focus();
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < length - 1) {
        e.preventDefault();
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      let pastedData = e.clipboardData.getData("text");

      // Filter based on inputType
      if (inputType === "numeric") {
        pastedData = pastedData.replace(/\D/g, "");
      } else if (inputType === "alphanumeric") {
        pastedData = pastedData.replace(/[^a-zA-Z0-9]/g, "");
      }

      pastedData = pastedData.slice(0, length);
      onChange(pastedData);

      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    };

    const handleClick = (index: number) => {
      setActiveIndex(index);
      inputRefs.current[index]?.focus();
    };

    const halfLength = Math.ceil(length / 2);

    return (
      <Container ref={ref}>
        <InputGroup>
          <Group>
            {Array.from({ length: halfLength }).map((_, index) => {
              const isActive = activeIndex === index;
              const hasValue = !!value[index];

              return (
                <InputBox
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={value[index] || ""}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onClick={() => handleClick(index)}
                  disabled={disabled}
                  data-active={isActive}
                  data-has-value={hasValue}
                  data-invalid={error}
                />
              );
            })}
          </Group>
          <Group>
            {Array.from({ length: length - halfLength }).map((_, idx) => {
              const index = halfLength + idx;
              const isActive = activeIndex === index;
              const hasValue = !!value[index];

              return (
                <InputBox
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={value[index] || ""}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onClick={() => handleClick(index)}
                  disabled={disabled}
                  data-active={isActive}
                  data-has-value={hasValue}
                  data-invalid={error}
                />
              );
            })}
          </Group>
        </InputGroup>
      </Container>
    );
  },
);

CodeInput.displayName = "CodeInput";

export default CodeInput;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
`;

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InputBox = styled.input`
  width: 52px;
  height: 56px;
  padding: 8px 16px;
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  ${typo.b7r};
  color: ${colorSet.gray2};
  text-align: center;
  background: ${colorSet.white};
  transition: all 0.2s;
  caret-color: transparent;

  &::placeholder {
    color: ${colorSet.gray7};
  }

  &:hover:not(:disabled) {
    box-shadow:
      0px 0px 0px 1px #1479eb,
      0px 0px 0px 3px rgba(24, 123, 235, 0.2),
      0px 1px 2px rgba(0, 0, 0, 0.25);
  }

  &:focus {
    outline: none;
    border: 1px solid ${colorSet.blue1};
    box-shadow:
      0px 0px 0px 1px #1479eb,
      0px 0px 0px 3px rgba(24, 123, 235, 0.2),
      0px 1px 2px rgba(0, 0, 0, 0.25);
  }

  &[data-has-value="true"] {
    background: ${colorSet.white};
  }

  &[data-invalid="true"] {
    border-color: ${colorSet.red2};

    &:hover,
    &:focus {
      box-shadow:
        0px 0px 0px 1px ${colorSet.red2},
        0px 0px 0px 3px rgba(214, 76, 76, 0.2),
        0px 1px 2px rgba(0, 0, 0, 0.25);
    }
  }

  &:disabled {
    background: ${colorSet.gray10};
    border-color: ${colorSet.gray8};
    color: ${colorSet.gray7};
    cursor: not-allowed;
  }
`;
